import {
  collection,
  doc,
  getDocs,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import firebaseService from '../firebase/FirebaseService';
import { decryptMessageFromStorage } from '../../helpers/chatEncryption';
import { client } from '../apollo';
import { GET_OR_CREATE_CHAT, SEND_MESSAGE, MARK_CHAT_AS_READ } from '../authService/queries';
import { getBrand } from '../../constants/brands';

export interface ChatMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  createdAt: Date;
  read: boolean;
}

/** What a guest asked about before booking (spec 016 B40). Every part may be missing. */
export interface ChatInquiry {
  checkIn?: string;
  checkOut?: string;
  guests?: { adults?: number; children?: number; infants?: number; pets?: number };
}

export interface Chat {
  id: string;
  /** Missing on an inquiry chat until a booking joins it. */
  bookingId?: string;
  /** Written by the server for stays; missing on car chats. */
  kind?: 'booking' | 'inquiry';
  listingId?: string;
  listingName?: string;
  listingImageUrl?: string;
  inquiry?: ChatInquiry;
  guestId: string;
  hostId: string;
  lastMessage?: string;
  lastMessageAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  carId?: string;
  carMake?: string;
  carModel?: string;
  carImageUrl?: string;
  guestDeleted?: boolean;
  hostDeleted?: boolean;
  guestDisplayName?: string;
  hostDisplayName?: string;
  guestEmail?: string;
  hostEmail?: string;
  /** Checkout brand of the chat's booking, written by the server (spec 011 R2). */
  brandSite?: string;
}

/** A chat written before the server stamped a brand belongs to Autopilotee. */
const DEFAULT_CHAT_BRAND_SITE = 'autopilotee.com';

/**
 * Spec 011 R2: every brand shares one Firestore, so the chat lists keep only
 * the conversations of the brand this page runs on. A Lenvoy trip's chat never
 * shows (or counts as unread) on autopilotee.com, and the other way round.
 */
export function isChatOnBrand(
  chat: Pick<Chat, 'brandSite'>,
  brandSite: string = getBrand().brandSite,
): boolean {
  const chatBrand = chat.brandSite?.trim().toLowerCase() || DEFAULT_CHAT_BRAND_SITE;
  return chatBrand === brandSite.trim().toLowerCase();
}

export interface ChatWithLastMessage extends Chat {
  unreadCount?: number;
}

interface CreateChatPayload {
  bookingId: string;
  guestId: string;
  hostId: string;
  carId?: string;
  carMake?: string;
  carModel?: string;
  carImageUrl?: string;
}

const toDate = (value?: Timestamp | Date | null): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  return undefined;
};

class ChatService {
  private get firestore() {
    return firebaseService.getFirestore();
  }

  private get chatsCollection() {
    return collection(this.firestore, 'chats');
  }

  async getOrCreateChat(payload: CreateChatPayload): Promise<string> {
    const { bookingId, guestId, hostId, carId, carMake, carModel, carImageUrl } = payload;

    if (!guestId || !hostId) {
      throw new Error('Guest and host identifiers are required to create a chat');
    }

    try {
      const { data } = await client.mutate({
        mutation: GET_OR_CREATE_CHAT,
        variables: {
          input: {
            bookingId,
            guestId,
            hostId,
            carId: carId ?? undefined,
            carMake: carMake ?? undefined,
            carModel: carModel ?? undefined,
            carImageUrl: carImageUrl ?? undefined,
          },
        },
      });

      if (data?.getOrCreateChat?.success && data?.getOrCreateChat?.chatId) {
        return data.getOrCreateChat.chatId;
      } else {
        throw new Error(data?.getOrCreateChat?.message || 'Failed to get or create chat');
      }
    } catch (error: any) {
      const errorMessage =
        error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to get or create chat';
      throw new Error(errorMessage);
    }
  }

  async sendMessage(
    chatId: string,
    senderId: string,
    senderName: string,
    text: string,
    guestId: string,
    hostId: string,
  ) {
    try {
      const { data } = await client.mutate({
        mutation: SEND_MESSAGE,
        variables: {
          input: {
            chatId,
            text,
            senderId,
            senderName,
            guestId,
            hostId,
          },
        },
      });

      if (data?.sendMessage?.success) {
        // Message will be automatically received via Firestore real-time listener
      } else {
        throw new Error(data?.sendMessage?.message || 'Failed to send message');
      }
    } catch (error: any) {
      const errorMessage =
        error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to send message';
      throw new Error(errorMessage);
    }
  }

  subscribeToMessages(
    chatId: string,
    currentUserId: string,
    callback: (messages: ChatMessage[]) => void,
  ) {
    // Note: Firebase auth should be ensured before calling this method
    // The subscription will fail if not authenticated, and errors are handled in the callbacks
    const chatRef = doc(this.chatsCollection, chatId);
    const messagesCollection = collection(chatRef, 'messages');
    const q = query(messagesCollection, orderBy('createdAt', 'asc'));

    return onSnapshot(
      q,
      async (snapshot: any) => {
        if (!snapshot || !snapshot.docs) {
          callback([]);
          return;
        }

        try {
          const messages: ChatMessage[] = await Promise.all(
            snapshot.docs.map(async (doc: any) => {
              const data = doc.data();
              let text = data.text;

              // Decrypt message if it's encrypted
              if (data.encrypted && typeof text === 'string') {
                try {
                  // Try to parse and decrypt
                  text = await decryptMessageFromStorage(text, chatId);
                } catch (error) {
                  // Silently handle decryption errors - message might be old format or key missing
                  // Show original text if decryption fails (for backward compatibility)
                  // Only show error indicator if text looks like encrypted JSON
                  try {
                    JSON.parse(text);
                    // If it's valid JSON, it's probably encrypted but can't decrypt
                    text = '[Message cannot be decrypted]';
                  } catch {
                    // Not JSON, might be plain text (old message), use as-is
                    // Remove encrypted flag to prevent retry
                  }
                }
              }

              return {
                id: doc.id,
                text,
                senderId: data.senderId,
                senderName: data.senderName,
                createdAt: toDate(data.createdAt) ?? new Date(),
                read: data.read ?? false,
              };
            }),
          );

          callback(messages);
        } catch (error) {
          callback([]);
        }
      },
      (error: any) => {
        callback([]);
      },
    );
  }

  async markAsRead(chatId: string, userId: string) {
    try {
      const { data } = await client.mutate({
        mutation: MARK_CHAT_AS_READ,
        variables: { chatId },
      });

      if (!data?.markChatAsRead) {
        throw new Error('Failed to mark chat as read');
      }
    } catch (error: any) {
      const errorMessage =
        error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to mark chat as read';
      // Silently handle error - don't throw to prevent UI issues
    }
  }

  subscribeToUserChats(
    userId: string,
    callback: (chats: ChatWithLastMessage[]) => void,
    role?: 'guest' | 'host',
  ) {
    // Note: Firebase auth should be ensured before calling this method
    // The subscription will fail if not authenticated, and errors are handled in the callbacks

    // If role is specified, only subscribe to chats where user has that role
    if (role === 'guest') {
      const guestQuery = query(this.chatsCollection, where('guestId', '==', userId));
      return onSnapshot(
        guestQuery,
        (snapshot: any) => {
          if (!snapshot || !snapshot.docs) {
            callback([]);
            return;
          }
          try {
            const chats = this.toChatList(snapshot.docs, userId);
            callback(chats);
          } catch (error) {
            callback([]);
          }
        },
        (error: any) => {
          callback([]);
        },
      );
    }

    if (role === 'host') {
      const hostQuery = query(this.chatsCollection, where('hostId', '==', userId));
      return onSnapshot(
        hostQuery,
        (snapshot: any) => {
          if (!snapshot || !snapshot.docs) {
            callback([]);
            return;
          }
          try {
            const chats = this.toChatList(snapshot.docs, userId);
            callback(chats);
          } catch (error) {
            callback([]);
          }
        },
        (error: any) => {
          callback([]);
        },
      );
    }

    // No role specified - get both guest and host chats (original behavior)
    let latestGuestChats: ChatWithLastMessage[] = [];
    let latestHostChats: ChatWithLastMessage[] = [];

    const emit = () => {
      const merged = this.mergeChats(latestGuestChats, latestHostChats);
      callback(merged);
    };

    const guestQuery = query(this.chatsCollection, where('guestId', '==', userId));
    const hostQuery = query(this.chatsCollection, where('hostId', '==', userId));

    const guestUnsubscribe = onSnapshot(
      guestQuery,
      (guestSnapshot: any) => {
        if (!guestSnapshot || !guestSnapshot.docs) {
          latestGuestChats = [];
          emit();
          return;
        }
        try {
          latestGuestChats = this.toChatList(guestSnapshot.docs, userId);
          emit();
        } catch (error) {
          latestGuestChats = [];
          emit();
        }
      },
      (error: any) => {
        latestGuestChats = [];
        emit();
      },
    );

    const hostUnsubscribe = onSnapshot(
      hostQuery,
      (hostSnapshot: any) => {
        if (!hostSnapshot || !hostSnapshot.docs) {
          latestHostChats = [];
          emit();
          return;
        }
        try {
          latestHostChats = this.toChatList(hostSnapshot.docs, userId);
          emit();
        } catch (error) {
          latestHostChats = [];
          emit();
        }
      },
      (error: any) => {
        latestHostChats = [];
        emit();
      },
    );

    return () => {
      guestUnsubscribe();
      hostUnsubscribe();
    };
  }

  async getUserChats(userId: string, role?: 'guest' | 'host'): Promise<ChatWithLastMessage[]> {
    // If role is specified, only get chats where user has that role
    if (role === 'guest') {
      const snapshot = await getDocs(query(this.chatsCollection, where('guestId', '==', userId)));
      return this.toChatList(snapshot.docs, userId);
    }

    if (role === 'host') {
      const snapshot = await getDocs(query(this.chatsCollection, where('hostId', '==', userId)));
      return this.toChatList(snapshot.docs, userId);
    }

    // No role specified - get both guest and host chats (original behavior)
    const [guestSnapshot, hostSnapshot] = await Promise.all([
      getDocs(query(this.chatsCollection, where('guestId', '==', userId))),
      getDocs(query(this.chatsCollection, where('hostId', '==', userId))),
    ]);

    const guestChats = this.toChatList(guestSnapshot.docs, userId);
    const hostChats = this.toChatList(hostSnapshot.docs, userId);

    return this.mergeChats(guestChats, hostChats);
  }

  /** This brand's chats from a snapshot, newest first. */
  private toChatList(
    docs: QueryDocumentSnapshot<DocumentData>[],
    userId: string,
  ): ChatWithLastMessage[] {
    return docs
      .map(doc => this.mapChatDocument(doc, userId))
      .filter(chat => isChatOnBrand(chat))
      .sort((a, b) => (b.lastMessageAt?.getTime() ?? 0) - (a.lastMessageAt?.getTime() ?? 0));
  }

  private mapChatDocument(
    doc: QueryDocumentSnapshot<DocumentData>,
    userId: string,
  ): ChatWithLastMessage {
    const data = doc.data();
    // Determine if user is guest or host, then read the correct unreadCount field
    const isGuest = data.guestId === userId;
    const userKey = isGuest ? 'guest' : 'host';
    const unreadCount = data[`unreadCount_${userKey}`] ?? 0;

    return {
      id: doc.id,
      bookingId: data.bookingId ?? undefined,
      kind: data.kind === 'inquiry' || data.kind === 'booking' ? data.kind : undefined,
      listingId: data.listingId ?? undefined,
      listingName: data.listingName ?? undefined,
      listingImageUrl: data.listingImageUrl ?? undefined,
      inquiry: data.inquiry ?? undefined,
      guestId: data.guestId,
      hostId: data.hostId,
      lastMessage: data.lastMessage ?? '',
      lastMessageAt: toDate(data.lastMessageAt),
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
      carId: data.carId ?? undefined,
      carMake: data.carMake ?? undefined,
      carModel: data.carModel ?? undefined,
      carImageUrl: data.carImageUrl ?? undefined,
      guestDeleted: data.guestDeleted ?? false,
      hostDeleted: data.hostDeleted ?? false,
      guestDisplayName: data.guestDisplayName ?? undefined,
      hostDisplayName: data.hostDisplayName ?? undefined,
      guestEmail: data.guestEmail ?? undefined,
      hostEmail: data.hostEmail ?? undefined,
      brandSite: data.brandSite ?? undefined,
      unreadCount,
    };
  }

  private mergeChats(
    first: ChatWithLastMessage[],
    second: ChatWithLastMessage[],
  ): ChatWithLastMessage[] {
    const map = new Map<string, ChatWithLastMessage>();

    [...first, ...second].forEach(chat => {
      const existing = map.get(chat.id);
      if (!existing) {
        map.set(chat.id, chat);
      } else {
        map.set(chat.id, {
          ...existing,
          ...chat,
          unreadCount: Math.max(existing.unreadCount ?? 0, chat.unreadCount ?? 0),
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const aTime = a.lastMessageAt?.getTime() ?? 0;
      const bTime = b.lastMessageAt?.getTime() ?? 0;
      return bTime - aTime;
    });
  }
}

const chatService = new ChatService();

export default chatService;
